// DataContext.jsx — loads data.json + india_states.geojson once, exposes global filters + agg().
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { agg, filterProjects } from './aggregate.js';

const DataCtx = createContext(null);

export function DataProvider({ children }) {
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState(null);
  const [attainment, setAttainment] = useState(null);
  const [subdistricts, setSubdistricts] = useState(null);
  const [resultsChain, setResultsChain] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState(null);
  const [openProject, setOpenProject] = useState(null);
  const [error, setError] = useState(null);

  // Filters — URL-sync-able (we do that in App.jsx via query params)
  const [type, setType] = useState('All');
  const [regions, setRegions] = useState(new Set());
  const [bands, setBands] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const base = process.env.PUBLIC_URL || '';
        const [dRes, gRes, aRes, sRes, rRes, bRes] = await Promise.all([
          fetch(`${base}/data/data.json`),
          fetch(`${base}/data/india_states.geojson`),
          fetch(`${base}/data/goals_attainment.json`),
          fetch(`${base}/data/subdistricts.json`),
          fetch(`${base}/data/results_chain.json`),
          fetch(`${base}/data/beneficiaries.json`),
        ]);
        if (!dRes.ok) throw new Error('Failed to load data.json');
        setData(await dRes.json());
        if (gRes.ok) setGeo(await gRes.json());
        if (aRes.ok) setAttainment(await aRes.json());
        if (sRes.ok) setSubdistricts(await sRes.json());
        if (rRes.ok) setResultsChain(await rRes.json());
        if (bRes.ok) setBeneficiaries(await bRes.json());
      } catch (e) {
        setError(e.message || 'Failed to load');
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return filterProjects(data.allProjects, { type, regions, bands });
  }, [data, type, regions, bands]);

  const A = useMemo(() => (data ? agg(filtered) : null), [data, filtered]);

  const value = {
    data,
    geo,
    attainment,
    subdistricts,
    resultsChain,
    beneficiaries,
    openProject,
    setOpenProject,
    error,
    filters: { type, regions, bands },
    setType,
    setRegions,
    setBands,
    filtered,
    agg: A,
  };

  return <DataCtx.Provider value={value}>{children}</DataCtx.Provider>;
}

export function useData() {
  const v = useContext(DataCtx);
  if (!v) throw new Error('useData must be inside <DataProvider>');
  return v;
}
